import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation, inject } from '@angular/core';
import { Meta } from '@df/core/meta/meta';
import { Events } from '@df/core/service/event';
import { OffClickModule } from '@df/module-off-click/off-click.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { ToggleService } from '@df/ui/toggle/toggle.service';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { GtmTrackNavigationClickDirective } from '../../module/gtm/gtm-track-navigation-click.directive';
import { NPageHeader } from '../page/header/page-header.interface';

/**
 * Meganav component.
 *
 * @usage
 * ```html
 * <meganav items="content.meganav"></meganav>
 * ```
 */
@Component({
    selector: 'meganav',
    templateUrl: 'meganav.html',
    styleUrls: ['./meganav.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [OffClickModule, ToggleModule, ICmsClientModule, CommonModule, HrefComponent, GtmTrackNavigationClickDirective]
})
export class MeganavComponent extends UiComponent {
    readonly meganavToggleGroupId = NPageHeader.MEGANAV_GROUP_ID;

    @Input()
    tabIndex!: number;

    @Input()
    items!: NPageHeader.IMenuItem[];

    protected events = inject(Events);

    constructor(
        meta: Meta,
        readonly toggleService: ToggleService
    ) {
        super();

        this.subscriptions.add(meta.subscribe(() => this.toggleService.closeGroup(this.meganavToggleGroupId)));
    }

    get isCms(): boolean {
        return this.client.isCms && !this.client.isCmsPreview;
    }

    handleMouseEnter(index: number, item: NPageHeader.IMenuItem): void {
        if (!this.isCms) {
            if (item.columns.length > 0) {
                this.toggleService.open([this.meganavToggleGroupId, index + this.tabIndex * 20]);
                this.events.broadcast('meganav:open', { index, item });
            }
        }
    }

    handleMouseLeave(index: number): void {
        if (!this.isCms) {
            this.toggleService.close([this.meganavToggleGroupId, index + this.tabIndex * 20]);
        }
        this.items.forEach((item, index) => {
            if (item.columns.length <= 0) this.toggleService.close([this.meganavToggleGroupId, index + this.tabIndex * 20]);
        });
    }

    handleClick(index: number): void {
        if (this.isCms) {
            this.toggleService.toggle([this.meganavToggleGroupId, index + this.tabIndex * 20]);
            this.items.forEach((item, index) => {
                if (item.columns.length <= 0) this.toggleService.close([this.meganavToggleGroupId, index + this.tabIndex * 20]);
            });
        }
    }

    close(): void {
        this.toggleService.closeGroup(NPageHeader.MEGANAV_GROUP_ID, { exceptionItems: ['search'] });
    }
}
